import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineUserGroup, HiOutlineTag, HiOutlinePlus } from 'react-icons/hi';
import SelectTextField from '../../../../../molecules/SelectTextField';
import SectionCard from '../../shared/SectionCard';
import VariableTextField from '../../shared/VariableTextField';
import { ChannelPill } from '../../shared/channelVisuals';
import { MessageCompositionStudio } from '../useMessageCompositionStudio';

const initials = (login: string) =>
    login
        .split(/[.\s_-]+/)
        .map((p) => p.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');

const IdentitySection: React.FC<{ studio: MessageCompositionStudio }> = ({ studio }) => {
    const navigate = useNavigate();
    const { meta, groups, readOnly, updateMeta } = studio;
    if (!meta) return null;
    const group = groups.find((g) => g.id === meta.groupId) ?? null;
    const members = group?.users?.filter((u) => u.active) ?? [];
    const selectableGroups = groups.filter((g) => g.status === 'ACTIVE' || g.id === meta.groupId);

    return (
        <div className="flex flex-col gap-4">
            <SectionCard title="Sobre a composição" description="Como a composição aparece nas listas e para quem vai operá-la." icon={<HiOutlineTag size={18} />}>
                <div className="grid grid-cols-1 gap-4">
                    <VariableTextField
                        id="mt-name"
                        label="Nome da composição"
                        value={meta.name}
                        disabled={readOnly}
                        maxLength={120}
                        error={!readOnly && !meta.name.trim() ? 'Obrigatório' : undefined}
                        onChange={(name) => updateMeta({ name })}
                    />
                    <VariableTextField
                        id="mt-description"
                        label="Para que serve"
                        value={meta.description}
                        disabled={readOnly}
                        multiline
                        rows={2}
                        maxLength={240}
                        hint="Uma frase que explique quando a composição é usada."
                        onChange={(description) => updateMeta({ description })}
                    />
                    <div>
                        <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Canal</div>
                        <ChannelPill channel={meta.channel} />
                        <p className="text-[11px] text-gray-400 mt-1">O canal não muda depois de criado. Para outro canal, duplique a composição.</p>
                    </div>
                </div>
            </SectionCard>

            {meta.channel !== 'INTERNAL_MESSAGE' && (
                <SectionCard
                    title="Grupo responsável"
                    description="Quem está no grupo pode usar esta composição para enviar mensagens. O grupo é opcional."
                    icon={<HiOutlineUserGroup size={18} />}
                >
                    <div className="flex items-end gap-2">
                        <div className="flex-1 min-w-0">
                            <SelectTextField
                                id="mt-group"
                                label="Grupo"
                                value={meta.groupId ?? ''}
                                disabled={readOnly}
                                emptyLabel="Sem grupo (somente administradores)"
                                items={selectableGroups.map((g) => ({ value: g.id, label: g.status === 'INACTIVE' ? `${g.name} (inativo)` : g.name }))}
                                onChange={(e) => updateMeta({ groupId: e.target.value || null })}
                            />
                        </div>
                        {!readOnly && (
                            <button
                                type="button"
                                onClick={() => navigate('group/new', { relative: 'path' })}
                                className="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-md text-sm bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-100 dark:hover:bg-slate-600"
                            >
                                <HiOutlinePlus size={16} /> Criar grupo
                            </button>
                        )}
                    </div>
                    {group && (
                        <div className="mt-3 flex items-center gap-3">
                            <div className="flex -space-x-2">
                                {members.slice(0, 6).map((u) => (
                                    <span
                                        key={u.login}
                                        title={u.login}
                                        className="w-7 h-7 rounded-full bg-teal-100 text-teal-800 dark:bg-teal-800 dark:text-teal-100 text-[11px] font-semibold flex items-center justify-center ring-2 ring-white dark:ring-slate-800"
                                    >
                                        {initials(u.login)}
                                    </span>
                                ))}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {members.length ? `${members.length} pessoa(s) poderão operar este modelo` : 'O grupo ainda não tem membros'}
                            </span>
                        </div>
                    )}
                </SectionCard>
            )}
        </div>
    );
};

export default IdentitySection;
